import React, {
  useEffect,
  useMemo,
  useState,
  SyntheticEvent,
  forwardRef,
  useImperativeHandle,
  useCallback
} from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, ClickAwayListener, FormGroup, Paper, SxProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import Expand from '@mui/icons-material/ExpandMore';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import TwRadioButton from '../TwRadioButtons/TwRadioButtons';
import {
  endOfMonth,
  endOfWeek,
  endOfToday,
  formatISO,
  startOfYear,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
  startOfToday,
  startOfTomorrow,
  endOfTomorrow,
  startOfYesterday,
  addWeeks,
  endOfYesterday
} from 'date-fns';
import TwDateRangePicker from '../TwDateRangePicker/TwDateRangePicker';
import { DateRange } from '@mui/lab/DateRangePicker';
import TwCheckbox from '../TwCheckboxes/TwCheckboxes';
import { useGetUrlFilterParam } from '../../hooks/useGetUrlFilterParam';

export const hasIdProperty = (obj: any): obj is { id: string } => {
  return obj && typeof obj === 'object' && 'id' in obj;
};

export const hasIdAndLabelProperty = (obj: any): obj is { id: string; label: string } => {
  return obj && typeof obj === 'object' && 'id' in obj && 'label' in obj;
};

export const hasIdAndSubLabelProperty = (obj: any): obj is { id: string; subLabel: string } => {
  return obj && typeof obj === 'object' && 'id' in obj && 'subLabel' in obj;
};

export const hasValueProperty = (obj: any): obj is { value: string } => {
  return obj && typeof obj === 'object' && 'value' in obj;
};

export const hasGroupByProperty = (obj: any): obj is { groupBy: string } => {
  return obj && typeof obj === 'object' && 'groupBy' in obj;
};

function isDateRangeOption(
  option: SelectedOptionsType
): option is { id: DateRangeFilterType; from: string; to: string } {
  return (
    typeof option === 'object' &&
    option !== null &&
    'id' in option &&
    'from' in option &&
    'to' in option
  );
}

export enum TwFilterTypes {
  typeahead = 'typeahead',
  multipleSelection = 'multipleSelection',
  dateRange = 'dateRange',
  slider = 'slider',
  multipleTypeahead = 'multipleTypeahead',
  customDateOnly = 'customDateOnly',
  radioOptions = 'radioOptions'
}

export enum DateRangeFilterType {
  TODAY = 'TODAY',
  TOMORROW = 'TOMORROW',
  YESTERDAY = 'YESTERDAY',
  TODAY_TOMORROW = 'TODAY_TOMORROW',
  YESTERDAY_TODAY = 'YESTERDAY_TODAY',
  YESTERDAY_TODAY_TOMORROW = 'YESTERDAY_TODAY_TOMORROW',
  THIS_WEEK = 'THIS_WEEK',
  NEXT_WEEK = 'NEXT_WEEK',
  PREVIOUS_WEEK = 'PREVIOUS_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  YEAR_TO_DATE = 'YEAR_TO_DATE',
  CUSTOM = 'CUSTOM'
}

export type SelectedOptionsType =
  | { [key: string]: string | boolean }
  | TwAutocompleteOption
  | TwAutocompleteOption[]
  | { id: DateRangeFilterType; from: string; to: string }
  | null;

export interface TwFilterProps {
  name: string;
  title?: string | number | undefined;
  filterType: TwFilterTypes;
  typeaheadOptionList?: TwAutocompleteOption[];
  multipleOptionList?: string[];
  onSelectedOptions: (searchParams: SelectedOptionsType) => void;
  onClear?: () => void;
  isClearAllActivated?: boolean;
  setIsClearAllActivated?: React.Dispatch<React.SetStateAction<boolean>>;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  open?: boolean;
  initial?: SelectedOptionsType;
  onInputChange?: (event: SyntheticEvent<Element, Event>, value: string | null) => void;
  multipleTypeaheadOptionList?: TwAutocompleteOption[];
  grouped?: boolean;
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: any) => React.ReactNode;
  disableFilter?: boolean;
  filterOptions?: (options: TwAutocompleteOption[]) => TwAutocompleteOption[];
  sx?: SxProps;
  sxFilterButton?: SxProps;
  radioOptionList?: { label: string; value: string }[];
  urlKey?: string;
  canBeHidden?: boolean;
  onFilterHide?: () => void;
  hideFutureDateOptions?: boolean;
}

export type TwFilterExposedRef = {
  clearFilter: () => void;
};

const getDateRangeFilterTypeLabel = (type: DateRangeFilterType): string | null => {
  switch (type) {
    case DateRangeFilterType.TODAY:
      return 'Today';
    case DateRangeFilterType.TOMORROW:
      return 'Tomorrow';
    case DateRangeFilterType.YESTERDAY:
      return 'Yesterday';
    case DateRangeFilterType.TODAY_TOMORROW:
      return 'Today & Tomorrow';
    case DateRangeFilterType.YESTERDAY_TODAY:
      return 'Yesterday & Today';
    case DateRangeFilterType.YESTERDAY_TODAY_TOMORROW:
      return 'Yesterday, Today & Tomorrow';
    case DateRangeFilterType.THIS_WEEK:
      return 'This week';
    case DateRangeFilterType.NEXT_WEEK:
      return 'Next week';
    case DateRangeFilterType.PREVIOUS_WEEK:
      return 'Previous week';
    case DateRangeFilterType.LAST_MONTH:
      return 'Last month';
    case DateRangeFilterType.YEAR_TO_DATE:
      return 'Year to date';
    default:
      return null;
  }
};

const FilterWrapper = styled('div')(() => ({
  position: 'relative'
}));

export const FilterButton = styled(Button)(() => ({
  minWidth: '80px',
  fontSize: '14px',
  fontFamily: 'Poppins',
  fontStyle: 'Regular',
  fontWeight: 400,
  textTransform: 'none',
  backgroundColor: '#fff',
  padding: '0 8px',
  border: '1px solid #E5E7EB',
  color: '#1F2937'
}));

const OptionsContainer = styled(Paper)(() => ({
  position: 'absolute',
  padding: '12px',
  paddingBottom: '10px',
  minWidth: '250px',
  zIndex: 100
}));

const OptionsTitle = styled('span')(() => ({
  fontSize: '14px',
  fontFamily: 'Poppins',
  fontStyle: 'Regular',
  fontWeight: 400,
  color: 'gray'
}));

const OptionsTitleContainer = styled('span')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '4px'
}));

const ClearButton = styled('span')(() => ({
  fontSize: '14px',
  fontFamily: 'Poppins',
  fontStyle: 'Regular',
  fontWeight: 400,
  color: '#3959df',
  cursor: 'pointer'
}));

const CustomRangeContainer = styled('div')(() => ({
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: '21px',
  color: '#12937',
  borderTop: '1px solid #e5e7eb',
  paddingTop: '12px'
}));

const NoBorderCustomRangeContainer = styled('div')(() => ({
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: '21px',
  color: '#12937'
}));

const TwFilters = forwardRef<TwFilterExposedRef, TwFilterProps>(
  (
    {
      name,
      title,
      filterType,
      typeaheadOptionList,
      multipleOptionList,
      onSelectedOptions,
      onClear,
      onClick,
      multipleTypeaheadOptionList,
      open,
      onInputChange,
      initial = null,
      grouped,
      renderOption,
      disableFilter,
      isClearAllActivated,
      setIsClearAllActivated,
      filterOptions,
      sx,
      sxFilterButton,
      radioOptionList,
      urlKey = null,
      canBeHidden = false,
      onFilterHide,
      hideFutureDateOptions = false
    },
    ref
  ) => {
    const [optionContainerRightPx, setOptionContainerRightPx] = useState<string>('auto');
    const [displayCustomRangeOption, setDisplayCustomRangeOption] = useState<boolean>(false);
    const [expandFilter, setExpandFilter] = useState<boolean>(false);
    const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date | null>>([
      null,
      null
    ]);
    const [hideNoOptions, setHideNoOptions] = useState<boolean>(true);
    const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsType>(null);

    const optionContainerRef = React.useRef<HTMLDivElement>(null);

    const urlFilterParam = useGetUrlFilterParam(urlKey);

    useEffect(() => {
      setOptionContainerRightPx(
        optionContainerRef?.current?.getBoundingClientRect().right &&
          optionContainerRef?.current?.getBoundingClientRect().right >= window.innerWidth
          ? `0px`
          : 'auto'
      );
    }, [expandFilter]);

    useEffect(() => {
      setSelectedOptions(initial ? initial : null);

      if (initial && 'id' in initial && initial.id === DateRangeFilterType.CUSTOM) {
        const values = initial as { id: DateRangeFilterType; from: string; to: string };
        setSelectedDateRange([
          values.from ? new Date(values.from.replace(/-/g, '/')) : null,
          values.to ? new Date(values.to.replace(/-/g, '/')) : null
        ]);
        setDisplayCustomRangeOption(true);
      } else if (!initial) {
        setSelectedDateRange([null, null]);
        setDisplayCustomRangeOption(false);
      }
    }, [open, initial]);

    useEffect(() => {
      open !== undefined && setExpandFilter(open);
    }, [open]);

    useEffect(() => {
      if (!urlKey) return;
      if (initial && !urlFilterParam) return;
      if (Array.isArray(urlFilterParam) && urlFilterParam.length < 1) return;

      setSelectedOptions(initial ? initial : urlFilterParam);
    }, [urlFilterParam]);

    useEffect(() => {
      if (isClearAllActivated) {
        clear();
        setIsClearAllActivated && setIsClearAllActivated(false);
      }
    }, [isClearAllActivated, setIsClearAllActivated]);

    const clear = useCallback((): void => {
      if (!urlKey) setSelectedOptions(null);
      setSelectedDateRange([null, null]);
      setDisplayCustomRangeOption(false);
      setExpandFilter(false);
      onClear && onClear();
    }, [onClear]);

    useImperativeHandle(
      ref,
      () => ({
        clearFilter(): void {
          clear();
        }
      }),
      [clear]
    );

    const handleSelectedOptionsChange = useCallback(
      (newValue: SelectedOptionsType) => {
        setExpandFilter(false);
        setSelectedOptions(newValue);
        setExpandFilter(false); // Close the filter after selecting an option
        if (typeof onSelectedOptions === 'function') {
          onSelectedOptions(newValue);
        }
      },
      [onSelectedOptions]
    );

    const handleFilterClick = useCallback(
      (event: React.MouseEvent<HTMLElement>): void => {
        setExpandFilter((prev) => !prev);
        event.stopPropagation();
        onClick && onClick(event);
      },
      [onClick]
    );

    const handleClickAway = useCallback((): void => {
      setExpandFilter(false);
    }, []);

    const handleInputChange = useCallback(
      (e: SyntheticEvent<Element, Event>, value: string | null): void => {
        onInputChange && onInputChange(e, value);
        setHideNoOptions(value === '' ? true : false);
      },
      [onInputChange]
    );

    const doDateRangeFilter = useCallback(
      (
        filterType: DateRangeFilterType,
        dateRange?: DateRange<Date>,
        dateRepresentation?: boolean
      ): void => {
        let newSelectedOptions: { id: DateRangeFilterType; from: string; to: string };

        if (
          filterType === DateRangeFilterType.CUSTOM &&
          dateRange &&
          dateRange[0] &&
          dateRange[1]
        ) {
          newSelectedOptions = {
            id: DateRangeFilterType.CUSTOM,
            from: formatISO(dateRange[0], {
              representation: dateRepresentation ? 'date' : 'complete'
            }),
            to: formatISO(dateRange[1], {
              representation: dateRepresentation ? 'date' : 'complete'
            })
          };
        } else {
          let from: Date, to: Date;

          switch (filterType) {
            case DateRangeFilterType.TODAY:
              from = startOfToday();
              to = endOfToday();
              break;

            case DateRangeFilterType.TOMORROW:
              from = startOfTomorrow();
              to = endOfTomorrow();
              break;

            case DateRangeFilterType.YESTERDAY:
              from = startOfYesterday();
              to = endOfYesterday();
              break;

            case DateRangeFilterType.TODAY_TOMORROW:
              from = startOfToday();
              to = endOfTomorrow();
              break;

            case DateRangeFilterType.YESTERDAY_TODAY:
              from = startOfYesterday();
              to = endOfToday();
              break;

            case DateRangeFilterType.YESTERDAY_TODAY_TOMORROW:
              from = startOfYesterday();
              to = endOfTomorrow();
              break;

            case DateRangeFilterType.THIS_WEEK:
              from = startOfWeek(new Date(), { weekStartsOn: 1 }); // Monday
              to = endOfWeek(new Date(), { weekStartsOn: 1 }); // Sunday
              break;

            case DateRangeFilterType.NEXT_WEEK:
              from = startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 }); // Next Monday
              to = endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 }); // Next Sunday
              break;
            case DateRangeFilterType.PREVIOUS_WEEK:
              from = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
              to = endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
              break;
            case DateRangeFilterType.LAST_MONTH:
              from = startOfMonth(subMonths(new Date(), 1));
              to = endOfMonth(subMonths(new Date(), 1));
              break;
            case DateRangeFilterType.YEAR_TO_DATE:
              from = startOfYear(new Date());
              to = new Date();
              break;
            default:
              return;
          }

          newSelectedOptions = {
            id: filterType,
            from: formatISO(from, { representation: 'complete' }),
            to: formatISO(to, { representation: 'complete' })
          };
        }

        setSelectedOptions(newSelectedOptions);
        handleSelectedOptionsChange(newSelectedOptions);
      },
      [handleSelectedOptionsChange]
    );

    const doMultipleSelectionFilter = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSelectedOptions((prev) => {
          const newOptions = {
            ...prev,
            [event.target.name]: event.target.checked
          };
          handleSelectedOptionsChange(newOptions);
          return newOptions;
        });
      },
      [handleSelectedOptionsChange]
    );

    const filterButtonLabel = useMemo<string>(() => {
      if (!selectedOptions) return name;

      if (Array.isArray(selectedOptions)) {
        // Handle multipleTypeahead
        const selectedCount = selectedOptions.length;
        if (selectedCount === 0) return name;
        if (selectedCount === 1) return `${name}: ${selectedOptions[0].label}`;
        return `${name}: ${selectedOptions[0].label} +${selectedCount - 1}`;
      }

      if (typeof selectedOptions === 'object') {
        if (isDateRangeOption(selectedOptions)) {
          // Handle dateRange
          const range = `${selectedOptions.from.replace(/-/g, '/')} - ${selectedOptions.to.replace(
            /-/g,
            '/'
          )}`;

          if (selectedOptions.id === DateRangeFilterType.CUSTOM) {
            return `${name}: ${range}`;
          }

          const label = getDateRangeFilterTypeLabel(selectedOptions.id);
          return `${name}: ${label ? label : range}`;
        }

        if ('label' in selectedOptions && typeof selectedOptions.label === 'string') {
          // Handle single selection (typeahead or radio)
          return `${name}: ${selectedOptions.label}`;
        }

        // Handle multipleSelection
        const multipleSelectionOptions = selectedOptions as { [key: string]: string | boolean };
        const selected = Object.keys(multipleSelectionOptions).filter(
          (k) => multipleSelectionOptions[k]
        );
        if (selected.length === 0) return name;
        if (selected.length === 1) return `${name}: ${selected[0]}`;
        return `${name}: ${selected[0]} +${selected.length - 1}`;
      }

      return name;
    }, [name, selectedOptions, urlFilterParam]);

    const handleRadioChecked = (item: { label: string; value: string }): boolean => {
      if (!selectedOptions) return false;

      if (Array.isArray(selectedOptions)) {
        return selectedOptions.some((option) => option.value === item.value);
      }

      return (
        ('value' in selectedOptions && selectedOptions.value === item.value) ||
        ('label' in selectedOptions && selectedOptions.label === item.label)
      );
    };

    const getPastAndTodayDateOptions = (): DateRangeFilterType[] => {
      return [
        DateRangeFilterType.TODAY,
        DateRangeFilterType.YESTERDAY,
        DateRangeFilterType.YESTERDAY_TODAY,
        DateRangeFilterType.THIS_WEEK,
        DateRangeFilterType.PREVIOUS_WEEK,
        DateRangeFilterType.LAST_MONTH,
        DateRangeFilterType.YEAR_TO_DATE
      ];
    };

    const getAllDateOptions = (): DateRangeFilterType[] => {
      return [
        DateRangeFilterType.TODAY,
        DateRangeFilterType.TOMORROW,
        DateRangeFilterType.YESTERDAY,
        DateRangeFilterType.TODAY_TOMORROW,
        DateRangeFilterType.YESTERDAY_TODAY_TOMORROW,
        DateRangeFilterType.THIS_WEEK,
        DateRangeFilterType.NEXT_WEEK,
        DateRangeFilterType.PREVIOUS_WEEK,
        DateRangeFilterType.LAST_MONTH,
        DateRangeFilterType.YEAR_TO_DATE
      ];
    };

    const renderFilterContent = useMemo(() => {
      switch (filterType) {
        case TwFilterTypes.typeahead:
          return (
            <TwAutocomplete
              name={`tw-typeahead-${name}`}
              optionList={typeaheadOptionList || []}
              value={selectedOptions}
              disableNoOption={hideNoOptions}
              onChange={(_e, value): void => handleSelectedOptionsChange(value)}
              useAsFilter={true}
              onInputChange={handleInputChange}
              grouped={grouped}
              renderOption={renderOption}
              filterOptions={filterOptions}
              autoFocus={true}
            />
          );
        case TwFilterTypes.multipleTypeahead:
          return (
            <TwAutocomplete
              multiple={true}
              filterSelectedOptions={true}
              defaultValue={[]}
              limitTags={2}
              freeText={true}
              name={name}
              optionList={(multipleTypeaheadOptionList as TwAutocompleteOption[]) || []}
              sortList={false}
              onChange={(_e, options): void => {
                if (!options) return;

                const optionsArr = options as unknown as TwAutocompleteOption[];
                const onlyValidObjects = optionsArr.filter(
                  (option) => typeof options === 'object' && hasValueProperty(option)
                );

                handleSelectedOptionsChange(onlyValidObjects as SelectedOptionsType);
              }}
              useAsFilter={true}
              sx={{
                minWidth: '250px',
                '& .MuiAutocomplete-inputRoot': {
                  padding: '4px 45px 9px 9px !important'
                }
              }}
              value={
                selectedOptions
                  ? Array.isArray(selectedOptions)
                    ? selectedOptions
                    : [selectedOptions]
                  : undefined
              }
              onInputChange={handleInputChange}
              grouped={grouped} // Added this line
              renderOption={renderOption} // Added this line
              filterOptions={filterOptions} // Added this line
              autoFocus={true}
            />
          );
        case TwFilterTypes.multipleSelection:
          return (
            <FormGroup>
              {multipleOptionList?.map((item) => (
                <TwCheckbox
                  key={item}
                  checked={
                    selectedOptions
                      ? (selectedOptions[item as keyof typeof selectedOptions] as boolean)
                      : undefined
                  }
                  onInputChange={doMultipleSelectionFilter}
                  label={item}
                  name={item}
                />
              ))}
            </FormGroup>
          );
        case TwFilterTypes.dateRange:
          const dateOptions = hideFutureDateOptions
            ? getPastAndTodayDateOptions()
            : getAllDateOptions();

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {dateOptions.map((dateType) => (
                <TwRadioButton
                  key={dateType}
                  value={dateType}
                  checked={isDateRangeOption(selectedOptions) && selectedOptions.id === dateType}
                  name={dateType.toLowerCase()}
                  label={getDateRangeFilterTypeLabel(dateType) || ''}
                  onInputChange={(): void => doDateRangeFilter(dateType)}
                />
              ))}

              <CustomRangeContainer>
                {displayCustomRangeOption ? (
                  <TwDateRangePicker
                    value={selectedDateRange}
                    onChange={(dateRange): void => {
                      setSelectedDateRange(dateRange);
                      doDateRangeFilter(DateRangeFilterType.CUSTOM, dateRange, true);
                    }}
                  />
                ) : (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center' }}
                    onClick={(): void => setDisplayCustomRangeOption(true)}
                  >
                    <AddIcon sx={{ marginRight: '6px', color: '#6B7280' }} /> Custom range
                  </Box>
                )}
              </CustomRangeContainer>
            </Box>
          );
        case TwFilterTypes.customDateOnly:
          return (
            <NoBorderCustomRangeContainer>
              <TwDateRangePicker
                value={selectedDateRange}
                onChange={(dateRange): void => {
                  setSelectedDateRange(dateRange);
                  doDateRangeFilter(DateRangeFilterType.CUSTOM, dateRange, true);
                }}
              />
            </NoBorderCustomRangeContainer>
          );
        case TwFilterTypes.radioOptions:
          return (
            <FormGroup>
              {radioOptionList?.map((item) => (
                <TwRadioButton
                  value={item.value}
                  key={item.value}
                  checked={handleRadioChecked(item)}
                  onInputChange={(): void => {
                    handleSelectedOptionsChange({ id: item.value, label: item.label });
                  }}
                  label={item.label}
                  name={item.value}
                  sx={{
                    lineHeight: '1.5rem',
                    paddingTop: '.5rem',
                    paddingBottom: '.5rem'
                  }}
                />
              ))}
            </FormGroup>
          );
        default:
          return null;
      }
    }, [
      filterType,
      name,
      typeaheadOptionList,
      selectedOptions,
      hideNoOptions,
      handleSelectedOptionsChange,
      handleInputChange,
      grouped,
      renderOption,
      filterOptions,
      multipleTypeaheadOptionList,
      multipleOptionList,
      doMultipleSelectionFilter,
      selectedDateRange,
      doDateRangeFilter,
      displayCustomRangeOption,
      radioOptionList
    ]);

    const getFilterIconAction = (): JSX.Element => {
      const endIconStyle = {
        fontSize: '16px',
        verticalAlign: 'middle',
        color: '#9CA3AF'
      };

      return (
        <>
          {canBeHidden ? (
            <div
              className="tid-filter-close"
              style={{
                fontSize: '14px'
              }}
              onClick={(e): void => {
                e.stopPropagation();
                onFilterHide && onFilterHide();
              }}
            >
              <CloseIcon sx={endIconStyle} />
            </div>
          ) : (
            <Expand sx={endIconStyle} />
          )}
        </>
      );
    };

    return (
      <FilterWrapper className={`tid-filter-${name}`} sx={sx}>
        <FilterButton
          className="tid-filter-label"
          endIcon={getFilterIconAction()}
          onClick={handleFilterClick}
          disabled={disableFilter}
          sx={sxFilterButton}
        >
          {filterButtonLabel}
        </FilterButton>

        {expandFilter && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <OptionsContainer
              sx={{
                minHeight: filterType === TwFilterTypes.dateRange ? '200px' : undefined,
                right: optionContainerRightPx
              }}
              ref={optionContainerRef}
            >
              <OptionsTitleContainer>
                {title && <OptionsTitle>{title}</OptionsTitle>}
                {selectedOptions && filterType !== TwFilterTypes.radioOptions && (
                  <ClearButton onClick={clear}>Clear</ClearButton>
                )}
              </OptionsTitleContainer>

              {renderFilterContent}
            </OptionsContainer>
          </ClickAwayListener>
        )}
      </FilterWrapper>
    );
  }
);

TwFilters.displayName = 'TwFilters';
export default TwFilters;
